/**
 * @description Create/edit discount form for the Products workspace Discounts tab (LYN-248).
 * @dependencies useFieldErrors, FieldError, discountErrors, productsService.getVariants.
 * @notes Persist only Wave 1 columns. Include list is products (and variants when target=variant).
 *        Empty include cannot fire — order/product require a product, variant requires a variant.
 *        Conditions UI is requires_recurring + min_distinct_products only. Unused condition
 *        columns are passed through on edit so this form does not wipe them.
 *        fixed_amount value is cents; the input is dollars like ProductForm.
 */

import React, { useEffect, useMemo, useState } from 'react';
import productsService from '../../services/api/productsService';
import { DISCOUNT_ERRORS } from '../../constants/discountErrors';
import FieldError, { GeneralErrors } from '../shared/FieldError';
import useFieldErrors from '../../hooks/useFieldErrors';

const INCLUDE_PICKER_COPY = 'These products count toward R2';

function displayAmount(initialData) {
  if (!initialData || initialData.value == null || initialData.value === '') {
    return '';
  }
  if (initialData.valueType === 'fixed_amount') {
    return (Number(initialData.value) / 100).toFixed(2);
  }
  return String(initialData.value);
}

function toStoredValue(valueType, display) {
  const amount = Number(display);
  if (!Number.isFinite(amount)) {
    return undefined;
  }
  if (valueType === 'fixed_amount') {
    return Math.round(amount * 100);
  }
  return amount;
}

function selectedProductIdsFromTargets(targets) {
  const ids = new Set();
  (targets || []).forEach((row) => {
    const productId = Number(row.productId);
    if (Number.isFinite(productId)) {
      ids.add(productId);
    }
  });
  return Array.from(ids);
}

function selectedVariantsFromTargets(targets) {
  return (targets || [])
    .filter((row) => row.variantId)
    .map((row) => ({
      productId: Number(row.productId),
      variantId: String(row.variantId)
    }));
}

function unusedConditionsFrom(initialData) {
  const existing = initialData?.conditions || {};
  return {
    requiresAllVariants: existing.requiresAllVariants ?? null,
    requiresAnyProducts: existing.requiresAnyProducts ?? null,
    billingOptionIn: existing.billingOptionIn ?? null,
    subtotalMinCents: existing.subtotalMinCents ?? null,
    subtotalMaxCents: existing.subtotalMaxCents ?? null
  };
}

function buildWritePayload(formData, initialData) {
  const minDistinct = String(formData.minDistinctProducts).trim();
  const targets = formData.target === 'variant'
    ? formData.includeVariants.map((row) => ({
      productId: Number(row.productId),
      variantId: row.variantId
    }))
    : formData.includeProductIds.map((productId) => ({
      productId: Number(productId)
    }));

  return {
    title: formData.title.trim(),
    status: formData.status,
    method: formData.method,
    code: formData.method === 'code' ? formData.code.trim() : null,
    combination: formData.combination,
    valueType: formData.valueType,
    value: toStoredValue(formData.valueType, formData.valueDisplay),
    target: formData.target,
    conditions: {
      requiresRecurring: formData.requiresRecurring,
      minDistinctProducts: minDistinct === '' ? null : Number(minDistinct),
      ...unusedConditionsFrom(initialData)
    },
    targets
  };
}

const DiscountForm = ({
  onSubmit,
  onCancel,
  initialData = null,
  onFormChange,
  products = []
}) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    status: initialData?.status || 'active',
    method: initialData?.method || 'automatic',
    code: initialData?.code || '',
    combination: initialData?.combination || 'stack',
    valueType: initialData?.valueType || 'percentage',
    valueDisplay: displayAmount(initialData),
    target: initialData?.target || 'order',
    requiresRecurring: initialData?.conditions?.requiresRecurring === true,
    minDistinctProducts: initialData?.conditions?.minDistinctProducts != null
      ? String(initialData.conditions.minDistinctProducts)
      : '',
    includeProductIds: selectedProductIdsFromTargets(initialData?.targets),
    includeVariants: selectedVariantsFromTargets(initialData?.targets)
  });
  const [variantsByProduct, setVariantsByProduct] = useState({});
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { fieldErrors, generalErrors, handleApiErrors, handleValidationErrors, clearFieldError, clearErrors } = useFieldErrors();

  const isEditing = !!initialData;
  const productOptions = useMemo(
    () => (products || []).map((product) => ({
      id: Number(product.id),
      name: product.name,
      sku: product.sku
    })),
    [products]
  );

  useEffect(() => {
    if (!onFormChange) {
      return;
    }
    const baseline = {
      title: initialData?.title || '',
      status: initialData?.status || 'active',
      method: initialData?.method || 'automatic',
      code: initialData?.code || '',
      combination: initialData?.combination || 'stack',
      valueType: initialData?.valueType || 'percentage',
      valueDisplay: displayAmount(initialData),
      target: initialData?.target || 'order',
      requiresRecurring: initialData?.conditions?.requiresRecurring === true,
      minDistinctProducts: initialData?.conditions?.minDistinctProducts != null
        ? String(initialData.conditions.minDistinctProducts)
        : ''
    };
    const scalarChanged = Object.keys(baseline).some((key) => formData[key] !== baseline[key]);
    const baselineProductIds = selectedProductIdsFromTargets(initialData?.targets).slice().sort();
    const currentProductIds = formData.includeProductIds.slice().sort();
    const productsChanged = JSON.stringify(baselineProductIds) !== JSON.stringify(currentProductIds);
    const baselineVariants = selectedVariantsFromTargets(initialData?.targets)
      .map((row) => `${row.productId}:${row.variantId}`)
      .sort();
    const currentVariants = formData.includeVariants
      .map((row) => `${row.productId}:${row.variantId}`)
      .sort();
    const variantsChanged = JSON.stringify(baselineVariants) !== JSON.stringify(currentVariants);
    onFormChange(scalarChanged || productsChanged || variantsChanged);
  }, [formData, initialData, onFormChange]);

  useEffect(() => {
    if (formData.target !== 'variant') {
      return;
    }
    const productIds = formData.includeProductIds.filter((id) => !variantsByProduct[id]);
    if (productIds.length === 0) {
      return;
    }

    let cancelled = false;
    const loadVariants = async () => {
      setLoadingVariants(true);
      try {
        const entries = await Promise.all(productIds.map(async (productId) => {
          const variants = await productsService.getVariants(productId).catch(() => []);
          return [productId, Array.isArray(variants) ? variants : []];
        }));
        if (!cancelled) {
          setVariantsByProduct((prev) => {
            const next = { ...prev };
            entries.forEach(([productId, variants]) => {
              next[productId] = variants;
            });
            return next;
          });
        }
      } finally {
        if (!cancelled) {
          setLoadingVariants(false);
        }
      }
    };
    loadVariants();
    return () => {
      cancelled = true;
    };
  }, [formData.target, formData.includeProductIds, variantsByProduct]);

  const validateForm = () => {
    const next = {};
    if (!formData.title.trim()) {
      next.title = 'Enter a name for this discount.';
    }
    if (formData.method === 'code' && !formData.code.trim()) {
      next.code = 'Enter a discount code, or switch this discount to apply automatically.';
    }
    const storedValue = toStoredValue(formData.valueType, formData.valueDisplay);
    if (storedValue === undefined) {
      next.value = 'Enter a discount amount.';
    } else if (formData.valueType === 'percentage' && (storedValue < 0 || storedValue > 100)) {
      next.value = 'Percent off must be between 0 and 100.';
    } else if (formData.valueType === 'fixed_amount' && storedValue < 0) {
      next.value = 'Amount off cannot be negative.';
    }
    if (formData.target === 'variant') {
      if (formData.includeVariants.length === 0) {
        next.targets = DISCOUNT_ERRORS.TARGETS_VARIANT_REQUIRED.message;
      }
    } else if (formData.includeProductIds.length === 0) {
      next.targets = DISCOUNT_ERRORS.TARGETS_REQUIRED.message;
    }
    const minDistinct = String(formData.minDistinctProducts).trim();
    if (minDistinct !== '' && (!Number.isInteger(Number(minDistinct)) || Number(minDistinct) < 1)) {
      next.minDistinctProducts = 'Enter a whole number of 1 or more, or leave this blank.';
    }
    handleValidationErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearErrors();
    if (!validateForm()) {
      return;
    }
    try {
      setIsSubmitting(true);
      await onSubmit(buildWritePayload(formData, initialData));
    } catch (error) {
      handleApiErrors(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    clearFieldError(name);
    if (name === 'valueDisplay') {
      clearFieldError('value');
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMethodChange = (event) => {
    const method = event.target.value;
    clearFieldError('method');
    clearFieldError('code');
    setFormData((prev) => ({
      ...prev,
      method,
      code: method === 'automatic' ? '' : prev.code
    }));
  };

  const handleTargetChange = (event) => {
    const target = event.target.value;
    clearFieldError('target');
    clearFieldError('targets');
    setFormData((prev) => ({
      ...prev,
      target,
      includeVariants: target === 'variant' ? prev.includeVariants : []
    }));
  };

  const toggleProduct = (productId) => {
    clearFieldError('targets');
    setFormData((prev) => {
      const selected = prev.includeProductIds.includes(productId);
      return {
        ...prev,
        includeProductIds: selected
          ? prev.includeProductIds.filter((id) => id !== productId)
          : [...prev.includeProductIds, productId],
        includeVariants: selected
          ? prev.includeVariants.filter((row) => row.productId !== productId)
          : prev.includeVariants
      };
    });
  };

  const toggleVariant = (productId, variantId) => {
    clearFieldError('targets');
    setFormData((prev) => {
      const exists = prev.includeVariants.some(
        (row) => row.productId === productId && String(row.variantId) === String(variantId)
      );
      const includeProductIds = prev.includeProductIds.includes(productId)
        ? prev.includeProductIds
        : [...prev.includeProductIds, productId];
      return {
        ...prev,
        includeProductIds,
        includeVariants: exists
          ? prev.includeVariants.filter(
            (row) => !(row.productId === productId && String(row.variantId) === String(variantId))
          )
          : [...prev.includeVariants, { productId, variantId: String(variantId) }]
      };
    });
  };

  const fieldError = (field) => fieldErrors[field] || null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <GeneralErrors errors={generalErrors} />

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Discount</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand ${
                fieldError('title') ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <FieldError error={fieldError('title')} />
          </div>

          <div>
            <label htmlFor="method" className="block text-sm font-medium text-gray-700 mb-1">
              Method <span className="text-red-500">*</span>
            </label>
            <select
              id="method"
              name="method"
              value={formData.method}
              onChange={handleMethodChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="automatic">Automatic</option>
              <option value="code">Code</option>
            </select>
            <FieldError error={fieldError('method')} />
          </div>

          {formData.method === 'code' && (
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                autoComplete="off"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand ${
                  fieldError('code') ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <FieldError error={fieldError('code')} />
            </div>
          )}

          <div>
            <label htmlFor="combination" className="block text-sm font-medium text-gray-700 mb-1">
              Combination <span className="text-red-500">*</span>
            </label>
            <select
              id="combination"
              name="combination"
              value={formData.combination}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="stack">Stack</option>
              <option value="exclusive">Exclusive</option>
            </select>
            <FieldError error={fieldError('combination')} />
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label htmlFor="valueType" className="block text-sm font-medium text-gray-700 mb-1">
              Amount type <span className="text-red-500">*</span>
            </label>
            <select
              id="valueType"
              name="valueType"
              value={formData.valueType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="percentage">Percent off</option>
              <option value="fixed_amount">Fixed amount</option>
            </select>
            <FieldError error={fieldError('valueType')} />
          </div>

          <div>
            <label htmlFor="valueDisplay" className="block text-sm font-medium text-gray-700 mb-1">
              {formData.valueType === 'fixed_amount' ? 'Amount off' : 'Percent off'} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="valueDisplay"
              name="valueDisplay"
              min="0"
              step={formData.valueType === 'fixed_amount' ? '0.01' : '1'}
              value={formData.valueDisplay}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand ${
                fieldError('value') ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <FieldError error={fieldError('value')} />
          </div>

          <div>
            <label htmlFor="target" className="block text-sm font-medium text-gray-700 mb-1">
              Target <span className="text-red-500">*</span>
            </label>
            <select
              id="target"
              name="target"
              value={formData.target}
              onChange={handleTargetChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="order">Order</option>
              <option value="product">Product</option>
              <option value="variant">Variant</option>
            </select>
            <FieldError error={fieldError('target')} />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Include list</h3>
        <p className="text-sm text-gray-500 mb-4">{INCLUDE_PICKER_COPY}</p>
        <FieldError error={fieldError('targets')} />
        {productOptions.length === 0 ? (
          <p className="text-sm text-gray-500">No products yet. Add products first, then include them here.</p>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
            {productOptions.map((product) => {
              const checked = formData.includeProductIds.includes(product.id);
              const variants = variantsByProduct[product.id] || [];
              return (
                <div key={product.id} className="border-b border-gray-100 last:border-b-0 pb-3 last:pb-0">
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleProduct(product.id)}
                      className="mt-1 h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-900">
                      {product.name}
                      {product.sku ? <span className="text-gray-500"> · {product.sku}</span> : null}
                    </span>
                  </label>
                  {formData.target === 'variant' && checked && (
                    <div className="ml-6 mt-2 space-y-1">
                      {loadingVariants && variants.length === 0 && (
                        <p className="text-xs text-gray-500">Loading variants…</p>
                      )}
                      {!loadingVariants && variants.length === 0 && (
                        <p className="text-xs text-gray-500">No variants on this product.</p>
                      )}
                      {variants.map((variant) => {
                        const variantId = String(variant.id);
                        const variantChecked = formData.includeVariants.some(
                          (row) => row.productId === product.id && String(row.variantId) === variantId
                        );
                        return (
                          <label key={variantId} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={variantChecked}
                              onChange={() => toggleVariant(product.id, variantId)}
                              className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">{variant.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Conditions</h3>
        <div className="space-y-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              id="requiresRecurring"
              name="requiresRecurring"
              checked={formData.requiresRecurring}
              onChange={handleChange}
              className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Requires recurring</span>
          </label>

          <div>
            <label htmlFor="minDistinctProducts" className="block text-sm font-medium text-gray-700 mb-1">
              Minimum distinct products
            </label>
            <input
              type="number"
              id="minDistinctProducts"
              name="minDistinctProducts"
              min="1"
              step="1"
              value={formData.minDistinctProducts}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand ${
                fieldError('minDistinctProducts') ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave blank if this discount does not need a distinct-product count.
            </p>
            <FieldError error={fieldError('minDistinctProducts')} />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-hover disabled:opacity-50 font-semibold transition-colors"
        >
          {isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Discount' : 'Create Discount')}
        </button>
      </div>
    </form>
  );
};

export { INCLUDE_PICKER_COPY, buildWritePayload };
export default DiscountForm;
